import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { baseUrl } from "../comman/config";
import { clearPermissions } from "../comman/permissions";

// The side bar, rendered from Screen Master and Department Master.
//
// This used to be two thousand lines of hand-written JSX: every group, every link and every
// `role == 4 ||` gate spelled out, so adding a screen or moving one between groups meant a code
// change and a deploy. Then it was a hard-coded copy of that tree, kept as a fallback. Neither is
// here any more.
//
// The menu arrives from /department_master/sidebar, already worked out: the headings are menu
// groups (menu_group_master), and which screens a person gets is decided by the departments they
// work in. What is left in this file is the chrome -- the drawer, the collapsible groups, and
// Logout.
//
// Logout stays hard-coded on purpose. It is not a screen, it has no route, and it is the one
// entry that must never be editable out of the menu.
//
// Profile and Change Password are screens now -- they sit under Account -- but they are still
// drawn beside Logout whenever the menu that arrived does not carry them. That covers every way
// they could otherwise vanish: a user whose departments do not include Account, an install that
// has not run the migrations, and a menu request that failed. Nobody should lose the way to their
// own password because of how a menu was arranged, or because a request timed out.

// The two icons the old side bar used, kept identical so the menu looks the same as it did.
// Departments got the trolley, screens the pie -- the only two in the file that were not dead
// code.
const DepartmentIcon = () => (
  <svg
    className="flex-shrink-0 w-5 h-5 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 18 21"
  >
    <path d="M15 12a1 1 0 0 0 .962-.726l2-7A1 1 0 0 0 17 3H3.77L3.175.745A1 1 0 0 0 2.208 0H1a1 1 0 0 0 0 2h.438l.6 2.255v.019l2 7 .746 2.986A3 3 0 1 0 9 17a2.966 2.966 0 0 0-.184-1h2.368c-.118.32-.18.659-.184 1a3 3 0 1 0 3-3H6.78l-.5-2H15Z" />
  </svg>
);

const ScreenIcon = () => (
  <svg
    className="w-5 h-5 text-gray-500 transition duration-75 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 22 21"
  >
    <path d="M16.975 11H10V4.025a1 1 0 0 0-1.066-.998 8.5 8.5 0 1 0 9.039 9.039.999.999 0 0 0-1-1.066h.002Z" />
    <path d="M12.5 0c-.157 0-.311.01-.565.027A1 1 0 0 0 11 1.02V10h8.975a1 1 0 0 0 1-.935c.013-.188.028-.374.028-.565A8.51 8.51 0 0 0 12.5 0Z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3 h-3 ${open ? "transform rotate-180" : ""}`}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 10 6"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="m1 1 4 4 4-4"
    />
  </svg>
);

export default function Sidebar() {
  const [showSublist, setShowSublist] = useState(false);
  // Which department groups are expanded. A Set rather than one open id, because the old side bar
  // let several groups stand open at once and closing one on opening another would be a change in
  // how the menu behaves, not just in where it comes from.
  const [openDepartments, setOpenDepartments] = useState(() => new Set());
  const [menu, setMenu] = useState([]);
  // The drawer layout, and the inverse of the `desk` breakpoint in tailwind.config.js -- the two
  // must agree, or a link tap would leave a menu open that the CSS has already docked, or close
  // one the CSS still has floating over the page. Height is in the test because a phone held
  // sideways is wide enough to look like a desktop and is not one.
  const isDrawerLayout = () =>
    !window.matchMedia('(min-width: 640px) and (min-height: 600px)').matches;
  const [isMobile, setIsMobile] = useState(isDrawerLayout);

  const navigate = useNavigate();
  const role = localStorage.getItem('userRole');
  // User Master can narrow one person within their role -- which departments they work in, and
  // which screens they get -- so the menu is asked for by user where there is one. A session that
  // signed in before User Master shipped has no stored id, and the backend falls back to the
  // role-only menu it has always served.
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    let cancelled = false;

    const getMenu = async () => {
      try {
        // role and user_id are attached to every request by comman/http.js; writing them here
        // too produced ?role=0&user_id=12&user_id=12&role=0, which Express reads as arrays.
        const response = await axios.get(`${baseUrl}/department_master/sidebar`);
        if (cancelled) return;
        // An empty menu from a request that worked is an answer, not a failure: the departments
        // are disabled, or this role may not see any of them. Only a request that did not come
        // back at all falls through to the copy below.
        setMenu(response.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;
        // No menu, and no substitute for one.
        //
        // There used to be a hard-coded copy here, kept as a life raft for a deploy that had not
        // run the migrations. It cannot be that any more: it describes a side bar whose headings
        // were departments and whose screens were gated by a roles column, and neither is true --
        // headings are menu groups now, and a department decides who reaches a screen. Showing it
        // would offer people screens they may not have and a shape the portal no longer has.
        //
        // Profile, Change Password and Logout are drawn below regardless, so somebody whose menu
        // failed to load can still reach their own account and sign out.
        console.warn(
          '[Sidebar] Could not load the menu from /department_master/sidebar. The side bar will ' +
          'be empty until the request succeeds -- check the API and reload.',
          error
        );
        setMenu([]);
      }
    };

    getMenu();
    return () => { cancelled = true; };
  }, [role, userId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isDrawerLayout());
    };

    // orientationchange as well as resize: turning a phone sideways swaps width for height, which
    // is exactly the thing this is watching, and not every browser fires resize for it.
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const toggleSublist = () => {
    setShowSublist(!showSublist);
  };

  const closeSidebar = () => {
    setShowSublist(false);
  };

  const toggleDepartment = (id) => {
    setOpenDepartments((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Function to handle menu item clicks
  const handleSubListClick = () => {
    // Only close sidebar on mobile
    if (isMobile) {
      setShowSublist(false);
    }
  };

  const MobileAwareLink = ({ to, children, className }) => {
    return (
      <Link
        to={to}
        className={className}
        onClick={handleSubListClick}
      >
        {children}
      </Link>
    );
  };

  // Every path the menu is already showing, so the two entries below are not drawn twice.
  const menuPaths = new Set(
    menu.flatMap((department) => (department.screens ?? []).map((screen) => screen.path))
  );

  const handleLogout = () => {
    // Clear all local storage
    localStorage.clear();
    // Permissions live in memory, not in storage, so localStorage.clear() does not reach them.
    // Without this the next person to sign in on this browser inherits the last one's answers
    // until their first navigation replaces them.
    clearPermissions();

    // Redirect to login page
    navigate('/login');
  };

  return (
    <>
      <button
        data-drawer-target="sidebar-multi-level-sidebar"
        data-drawer-toggle="sidebar-multi-level-sidebar"
        aria-controls="sidebar-multi-level-sidebar"
        onClick={toggleSublist}
        type="button"
        className="inline-flex items-center p-2 mt-2 ms-3 text-sm text-gray-500 rounded-lg desk:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
      >
        <span className="sr-only">Open sidebar</span>
        <svg
          className="w-6 h-6"
          aria-hidden="true"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            fillRule="evenodd"
            d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
          ></path>
        </svg>
      </button>

      <aside
        id="sidebar-multi-level-sidebar"
        className={`fixed top-0 left-0 z-40 w-72 h-screen transition-transform ${
          showSublist ? "translate-x-0" : "-translate-x-full"
        } desk:translate-x-0`}
        aria-label="Sidebar"
      >
        <button
          type="button"
          onClick={closeSidebar}
          className={`absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none ${
            showSublist && isMobile ? "block" : "hidden"
          }`}
          aria-label="Close sidebar"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="h-full px-3 py-4 overflow-y-auto bg-gray-50 dark:bg-gray-800">
          <ul className="space-y-2 font-medium mt-3">
            {menu.map((department) => {
              const open = openDepartments.has(department.id);
              return (
                <li key={department.id}>
                  <button
                    type="button"
                    onClick={() => toggleDepartment(department.id)}
                    aria-expanded={open}
                    className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                  >
                    <DepartmentIcon />
                    <span className="flex-1 ms-3 text-left rtl:text-right whitespace-nowrap">
                      {department.name}
                    </span>
                    <ChevronIcon open={open} />
                  </button>
                  <ul className={`${open ? "block" : "hidden"} py-2 space-y-2 ml-5 pl-3`}>
                    {department.screens.map((screen) => (
                      <li key={screen.id}>
                        <MobileAwareLink
                          to={screen.path}
                          className="flex items-center p-2 text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 group"
                        >
                          <ScreenIcon />
                          <span className="ms-3">{screen.name}</span>
                        </MobileAwareLink>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
            {/* Only when the menu did not bring them -- see the note at the top of the file. */}
            {!menuPaths.has("/profile") && (
              <li>
                <MobileAwareLink
                  to="/profile"
                  className="flex items-center p-2 text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 group"
                >
                  <DepartmentIcon />
                  <span className="ms-3">Profile</span>
                </MobileAwareLink>
              </li>
            )}
            {!menuPaths.has("/change_password") && (
              <li>
                <MobileAwareLink
                  to="/change_password"
                  className="flex items-center p-2 text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 group"
                >
                  <DepartmentIcon />
                  <span className="ms-3">Change Password</span>
                </MobileAwareLink>
              </li>
            )}
            <li>
              <button
                onClick={handleLogout}
                className="flex items-center p-2 text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 group"
              >
                <DepartmentIcon />
                <span className="ms-3">Logout</span>
              </button>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
}
